import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { BusinessProvider } from './contexts/BusinessContext';
import { PortalProvider } from './contexts/PortalContext';
import ProtectedRoute from './components/ProtectedRoute';
import { AdminRoute, OwnerRoute, EmployeeRoute } from './components/RoleBasedRoutes';
import Layout from './components/Layout';
import Dashboard from './pages/Dashboard';
import Customers from './pages/Customers';
import CustomerDetail from './pages/CustomerDetail';
import Jobs from './pages/Jobs';
import Invoices from './pages/Invoices';
import Expenses from './pages/Expenses';
import Leads from './pages/Leads';
import Settings from './pages/Settings';
import TeamManagement from './pages/TeamManagement';
import InviteEmployee from './pages/InviteEmployee';
import EmployeeRegistration from './pages/EmployeeRegistration';
import Login from './pages/Login';
import RegisterPage from './pages/RegisterPage';
import TrialExpiredPage from './pages/TrialExpiredPage';
import SubscriptionCancelled from './pages/SubscriptionCancelled';
import ScrollToTop from './components/ScrollToTop';

import PremiumCheckoutPage from './pages/PremiumCheckoutPage';
import EnhancedSuccessPage from './pages/EnhancedSuccessPage';
import Reports from './pages/Reports';
import SharedPhotos from './pages/SharedPhotos';

// Import marketing pages
import HomePage from './pages/HomePage';
import FeaturesPage from './pages/FeaturesPage';
import PricingPage from './pages/PricingPage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import ComingSoonPage from './pages/ComingSoonPage';

import TermsOfServicePage from './pages/TermsOfServicePage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';

import EmailTemplatesPage from './pages/EmailTemplatesPage';
import SignAgreementPage from './pages/SignAgreementsPage';
import ServiceAgreementsPage from './pages/ServiceAgreementsPage';
import ServiceTypesGuide from './pages/help/ServiceTypesGuide';

// Import portal pages
import PortalRoutes from './pages/portal/PortalRoutes';

// Help Center Pages
import HelpCenter from './pages/help/HelpCenter';
import CustomerPortalGuide from './pages/help/CustomerPortalGuide';
import PaymentsGuide from './pages/help/PaymentsGuide';
import GettingStartedGuide from './pages/help/GettingStartedGuide';
import InvoicesGuide from './pages/help/InvoicesGuide';
import BestPracticesGuide from './pages/help/BestPracticesGuide';
import EmailTemplatesGuide from './pages/help/EmailTemplatesGuide';
import ServiceAgreementsGuide from './pages/help/ServiceAgreementsGuide';
import TaxSettingsGuide from './pages/help/TaxSettingsGuide';
import ReportsGuide from './pages/help/ReportsGuide';
import AccountSecurityGuide from './pages/help/AccountSecurityGuide';
import SmsNotificationsGuide from './pages/help/SmsNotificationsGuide';
import QuoteAcceptanceGuide from './pages/help/QuoteAcceptanceGuide';
import PaymentReconciliationGuide from './pages/help/PaymentReconciliationGuide';

// Password Reset
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';

// Admin Pages
import AdminLayout from './layouts/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminBusinesses from './pages/admin/AdminBusinesses';
import AdminFinancials from './pages/admin/AdminFinancials';
import AdminExpenses from './pages/admin/AdminExpenses';
import AdminDocumentation from './pages/admin/AdminDocumentation';

// Business Owner Customer Payments
import PayInvoice from './pages/PayInvoice';
import PaymentSuccess from './pages/PaymentSuccess';

// trust guide
import TrustPage from './pages/TrustPage';
import SmsPolicyPage from './pages/SmsPolicyPage';

// ============================================
// PUBLIC LEAD CAPTURE PAGES
// ============================================
import PublicQuotePage from './pages/PublicQuotePage';
import EmbedQuotePage from './pages/EmbedQuotePage';
import QuoteAcceptPage from './pages/QuoteAcceptPage';

// ============================================
// EMAIL PREFERENCE MANAGEMENT - PUBLIC
// ============================================
import UnsubscribePage from './pages/UnsubscribePage';

import ConfirmPayment from './pages/ConfirmPayment';

// Satellite Quote Tool
import SatelliteMeasurePage from './pages/SatelliteMeasurePage';

function App() {
  // ============================================
  // 🚀 COMING SOON MODE TOGGLE
  // ============================================
  // Set to false: Show Coming Soon page to everyone
  // Set to true: Show full site with all features
  // ============================================
  const SITE_IS_LIVE = true; // ← Change this to true when ready to launch!

  // If site is not live, show coming soon page for ALL visitors
  // EXCEPT those with a beta code trying to register
  if (!SITE_IS_LIVE) {
    const hasBetaCode = window.location.search.includes('beta=');
    const isRegisterPage = window.location.pathname === '/register';
    const isLoginPage = window.location.pathname === '/login';
    const isPortalRoute = window.location.pathname.startsWith('/portal');
    const hasToken = localStorage.getItem('token'); // or however you store auth
    
    // Allow: beta registration, login page, portal routes, or already authenticated users
    if (!(hasBetaCode && isRegisterPage) && !isLoginPage && !isPortalRoute && !hasToken) {
      return (
        <BusinessProvider>
          <Router>
            <Routes>
              <Route path="/trust" element={<TrustPage comingSoonMode={true} />} />
              <Route path="*" element={<ComingSoonPage />} />
            </Routes>
          </Router>
        </BusinessProvider>
      );
    }
  }

  // ============================================
  // FULL SITE - Only shows when SITE_IS_LIVE = true
  // ============================================
  return (
    <BusinessProvider>
      <Router>
        <ScrollToTop /> 
        <Routes>
          {/* Public/Marketing routes */}
          <Route path="/" element={<HomePage />} />
          <Route path="/features" element={<FeaturesPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/confirm-payment/:token" element={<ConfirmPayment />} />
          <Route path="/shared/photos/:shareToken" element={<SharedPhotos />} />
          
          {/* ============================================
              PUBLIC LEAD CAPTURE ROUTES
              These are public pages where customers can 
              request quotes without logging in
              ============================================ */}
          <Route path="/request-quote/:slug" element={<PublicQuotePage />} />
          <Route path="/embed/quote/:slug" element={<EmbedQuotePage />} />

          {/* ============================================
              QUOTE ACCEPTANCE PAGE - PUBLIC
              Customers view and accept quotes via 
              tokenized link from quote email
              ============================================ */}
          <Route path="/quote/:token" element={<QuoteAcceptPage />} />
          
          {/* ============================================
              EMAIL PREFERENCE MANAGEMENT - PUBLIC
              Customers can manage email preferences via 
              token link from unsubscribe footer in emails
              ============================================ */}
          <Route path="/unsubscribe" element={<UnsubscribePage />} />
          
          {/* Auth routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/invite" element={<EmployeeRegistration />} /> {/* PUBLIC - Employee registration */}
          <Route path="/trial-expired" element={<TrialExpiredPage />} />
          <Route path="/subscription-cancelled" element={<SubscriptionCancelled />} />

          {/* 
            ============================================
            CUSTOMER PORTAL ROUTES - PUBLIC (Token-based auth)
            All portal routes wrapped in single PortalProvider
            so state persists across page navigation
            ============================================
          */}
          <Route path="/portal/*" element={<PortalRoutes />} />

          {/* Business owner customers payments */}
          <Route path="/pay/invoice/:token" element={<PayInvoice />} />
          <Route path="/pay/success" element={<PaymentSuccess />} />

          {/* Subscription routes */}
          <Route 
            path="/subscribe" 
            element={<PremiumCheckoutPage />}
          />
          <Route 
            path="/subscription-success" 
            element={
              <ProtectedRoute>
                <EnhancedSuccessPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/trust" 
            element={
            <TrustPage />
          } />

          <Route path="/terms" element={<TermsOfServicePage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/sms-policy" element={<SmsPolicyPage />} />

          {/* 
            ============================================
            PROTECTED APP ROUTES - ROLE-BASED ACCESS
            ============================================
            
            Dashboard: Owner + Employee (operational view)
            Customers: Owner + Employee (both can view/edit)
            Jobs: Owner + Employee (both can manage jobs)
            Leads: Owner + Employee (both can work with leads)
            
            Invoices: Owner only (financial)
            Expenses: Owner only (financial)
            Settings: Owner only (business configuration)
          */}

          {/* Dashboard - Owner + Employee */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <EmployeeRoute>
                  <Layout>
                    <Dashboard />
                  </Layout>
                </EmployeeRoute>
              </ProtectedRoute>
            }
          />

          {/* Customers - Owner + Employee */}
          <Route
            path="/customers"
            element={
              <ProtectedRoute>
                <EmployeeRoute>
                  <Layout>
                    <Customers />
                  </Layout>
                </EmployeeRoute>
              </ProtectedRoute>
            }
          />

          {/* Customer Detail - Owner + Employee */}
          <Route
            path="/customers/:id"
            element={
              <ProtectedRoute>
                <EmployeeRoute>
                  <Layout>
                    <CustomerDetail />
                  </Layout>
                </EmployeeRoute>
              </ProtectedRoute>
            }
          />

          {/* Leads - Owner + Employee */}
          <Route
            path="/leads"
            element={
              <ProtectedRoute>
                <EmployeeRoute>
                  <Layout>
                    <Leads />
                  </Layout>
                </EmployeeRoute>
              </ProtectedRoute>
            }
          />

          {/* Jobs - Owner + Employee */}
          <Route
            path="/jobs"
            element={
              <ProtectedRoute>
                <EmployeeRoute>
                  <Layout>
                    <Jobs />
                  </Layout>
                </EmployeeRoute>
              </ProtectedRoute>
            }
          />

          {/* SatMeasure - Owner + Employee (from Job context) */}
          <Route
            path="/jobs/:jobId/measure"
            element={
              <ProtectedRoute>
                <EmployeeRoute>
                  <SatelliteMeasurePage />
                </EmployeeRoute>
              </ProtectedRoute>
            }
          />

          {/* Invoices - Owner ONLY (Financial) */}
          <Route
            path="/invoices"
            element={
              <ProtectedRoute>
                <OwnerRoute>
                  <Layout>
                    <Invoices />
                  </Layout>
                </OwnerRoute>
              </ProtectedRoute>
            }
          />

          {/* Expenses - Owner ONLY (Financial) */}
          <Route
            path="/expenses"
            element={
              <ProtectedRoute>
                <OwnerRoute>
                  <Layout>
                    <Expenses />
                  </Layout>
                </OwnerRoute>
              </ProtectedRoute>
            }
          />

          {/* Reports - Owner ONLY */}
          <Route
            path="/reports"
            element={
              <ProtectedRoute>
                <OwnerRoute>
                  <Layout>
                    <Reports />
                  </Layout>
                </OwnerRoute>
              </ProtectedRoute>
            }
          />

          <Route 
            path="/email-templates" 
            element={
              <ProtectedRoute>
                <OwnerRoute>
                  <Layout>
                    <EmailTemplatesPage />
                  </Layout>
                </OwnerRoute>
              </ProtectedRoute>
            } 
          />

          {/* Public signing page - NO auth wrappers (customers access via email link) */}
          <Route path="/sign-agreement/:token" element={<SignAgreementPage />} />

          {/* Agreement management page - protected, owners only */}
          <Route 
            path="/agreements" 
            element={
              <ProtectedRoute>
                <OwnerRoute>
                  <Layout>
                    <ServiceAgreementsPage />
                  </Layout>
                </OwnerRoute>
              </ProtectedRoute>
            } 
          />

          {/* Settings - Owner ONLY (Configuration) */}
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <OwnerRoute>
                  <Layout>
                    <Settings />
                  </Layout>
                </OwnerRoute>
              </ProtectedRoute>
            }
          />

          <Route
            path="/help"
            element={
              <ProtectedRoute>
                <OwnerRoute>
                  <Layout>
                    <HelpCenter />
                  </Layout>
                </OwnerRoute>
              </ProtectedRoute>
            }
          />

          <Route
            path="/help/customer-portal"
            element={
              <ProtectedRoute>
                <OwnerRoute>
                  <Layout>
                    <CustomerPortalGuide />
                  </Layout>
                </OwnerRoute>
              </ProtectedRoute>
            }
          />

          <Route
            path="/help/payments"
            element={
              <ProtectedRoute>
                <OwnerRoute>
                  <Layout>
                    <PaymentsGuide />
                  </Layout>
                </OwnerRoute>
              </ProtectedRoute>
            }
          />
          
          <Route 
            path="/help/payment-reconciliation" 
            element={
              <ProtectedRoute>
                <OwnerRoute>
                  <Layout>
                    <PaymentReconciliationGuide />
                  </Layout>
                </OwnerRoute>
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/help/getting-started" 
            element={
              <ProtectedRoute>
                <OwnerRoute>
                  <Layout>
                    <GettingStartedGuide />
                  </Layout>
                </OwnerRoute>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/help/invoices" 
            element={
              <ProtectedRoute>
                <OwnerRoute>
                  <Layout>
                  <InvoicesGuide />
                  </Layout>
                </OwnerRoute>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/help/service-types" 
            element={
              <ProtectedRoute>
                <OwnerRoute>
                  <Layout>
                    <ServiceTypesGuide />
                  </Layout>
                </OwnerRoute>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/help/best-practices" 
            element={
              <ProtectedRoute>
                <OwnerRoute>
                  <Layout>
                    <BestPracticesGuide />
                  </Layout>
                </OwnerRoute>
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/help/email-templates" 
            element={
              <ProtectedRoute>
                <OwnerRoute>
                  <Layout>
                    <EmailTemplatesGuide />
                  </Layout>
                </OwnerRoute>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/help/service-agreements" 
            element={
              <ProtectedRoute>
                <OwnerRoute>
                  <Layout>
                    <ServiceAgreementsGuide />
                  </Layout>
                </OwnerRoute>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/help/tax-settings" 
            element={
              <ProtectedRoute>
                <OwnerRoute>
                  <Layout>
                    <TaxSettingsGuide />
                  </Layout>
                </OwnerRoute>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/help/account-security" 
            element={
              <ProtectedRoute>
                <OwnerRoute>
                  <Layout>
                    <AccountSecurityGuide />
                  </Layout>
                </OwnerRoute>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/help/reports" 
            element={
              <ProtectedRoute>
                <OwnerRoute>
                  <Layout>
                    <ReportsGuide />
                  </Layout>
                </OwnerRoute>
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/help/sms-notifications" 
            element={
              <ProtectedRoute>
                <OwnerRoute>
                  <Layout>
                    <SmsNotificationsGuide />
                  </Layout>
                </OwnerRoute>
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/help/quote-acceptance" 
            element={
              <ProtectedRoute>
                <OwnerRoute>
                  <Layout>
                    <QuoteAcceptanceGuide />
                  </Layout>
                </OwnerRoute>
              </ProtectedRoute>
            } 
          />

          {/* Team Management - Owner ONLY */}
          <Route
            path="/team"
            element={
              <ProtectedRoute>
                <OwnerRoute>
                  <Layout>
                    <TeamManagement />
                  </Layout>
                </OwnerRoute>
              </ProtectedRoute>
            }
          />

          {/* Invite Employee - Owner ONLY */}
          <Route
            path="/team/invite"
            element={
              <ProtectedRoute>
                <OwnerRoute>
                  <Layout>
                    <InviteEmployee />
                  </Layout>
                </OwnerRoute>
              </ProtectedRoute>
            }
          />

          {/* Admin Routes - ApplicationAdmin or Support only */}
          <Route 
            path="/admin" 
            element={
              <ProtectedRoute allowedRoles={['ApplicationAdmin', 'Support']}>
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<AdminDashboard />} />
            <Route path="businesses" element={<AdminBusinesses />} />
            <Route path="financials" element={<AdminFinancials />} />
            <Route path="expenses" element={<AdminExpenses />} />
            <Route path="documentation" element={<AdminDocumentation />} />
          </Route>

          {/* Catch all - redirect to home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    </BusinessProvider>
  );
}

export default App;